import { z } from 'zod/v4';
import {
  INormalText,
  NORMAL_TEXT_SCHEMA,
  NormalTextId,
  TextId,
  TextLanguageValueId,
} from './text/text.interfaces';
import { ETextType } from './text/text.enums';
import { EEntityType, ERelationType } from './text-relation-type.enums';
import { Brand } from '../branded-types';
import { LanguagePrefix } from './language.interfaces';

export type ImageId = Brand<string, 'imageId'>;
export type AccessibleImageId = Brand<string, 'accessibleImageId'>;

export const IMAGE_SCHEMA: z.ZodType<IImage> = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as ImageId)
    .optional(),
  filename: z.string(),
  fileExtension: z.string(),
  createdAt: z.coerce.date(),
  usage: z.string().nullish(),
});

export const IMAGES_SCHEMA: z.ZodType<IImage[]> = z.array(IMAGE_SCHEMA);

export interface IImage {
  id?: ImageId;
  filename: string;
  fileExtension: string;
  createdAt: Date;
  usage?: string | null;
}

export const ACCESSIBLE_IMAGE_SCHEMA: z.ZodType<IAccessibleImage> = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as AccessibleImageId)
    .optional(),
  imageAlts: z.array(NORMAL_TEXT_SCHEMA),
  image: IMAGE_SCHEMA.nullable().default(null),
  url: z.string().optional().nullable(),
  positionOrder: z.number(),
});
export interface IAccessibleImage {
  id?: AccessibleImageId;
  imageAlts: INormalText[];
  image: IImage | null;
  url?: string | null;
  positionOrder: number;
}
export const DEFAULT_ACCESSIBLE_IMAGE_LOGO: IAccessibleImage = {
  imageAlts: [
    {
      id: '1e18428c-08ef-4aa5-b7b7-72f59d245a6f' as NormalTextId,
      textType: ETextType.Web,
      relationType: ERelationType.imageAlt,
      entityType: EEntityType.header,
      text: {
        id: 'c4d65108-89bd-4f79-88d4-2e00b3aca587' as TextId,
        values: [
          {
            id: '54bafa1f-2cac-4333-9879-3e72463aada0' as TextLanguageValueId,
            value: 'Logo',
            languagePrefix: 'DE' as LanguagePrefix,
          },
        ],
      },
    },
  ],
  url: '',
  image: {
    id: 'a553f940-ecb6-40c5-b83c-69495d9bc235' as ImageId,
    filename: 'favicon',
    fileExtension: 'png',
    createdAt: new Date('2025-08-29 07:33:26.030897 +00:00'),
  },
  positionOrder: 0,
};

export const DEFAULT_ACCESSIBLE_IMAGE_BANNER: IAccessibleImage = {
  imageAlts: [
    {
      id: '43e11725-a5a8-4080-b5eb-39b3a9647981' as NormalTextId,
      textType: ETextType.Web,
      relationType: ERelationType.imageAlt,
      entityType: EEntityType.banner,
      text: {
        id: '880a22e0-ffcd-4ea0-bc2b-67a124cec811' as TextId,
        values: [
          {
            id: '93c20bf6-bfde-4194-93f8-c6b1f655a5a4' as TextLanguageValueId,
            value: 'Banner',
            languagePrefix: 'DE' as LanguagePrefix,
          },
        ],
      },
    },
  ],
  url: '',
  image: {
    id: '23e49042-1953-48c3-945c-68557eb9ba3c' as ImageId,
    filename: 'banner',
    fileExtension: 'png',
    createdAt: new Date('2025-08-29 07:33:26.030897 +00:00'),
  },
  positionOrder: 0,
};

export const DEFAULT_ACCESSIBLE_IMAGE_EMPTY: IAccessibleImage = {
  imageAlts: [],
  url: '',
  image: null,
  positionOrder: 0,
};
